/* eslint-disable no-param-reassign */
import { LitElement, html } from 'lit';
import { customElement, state, property } from 'lit/decorators.js';
import Storefront from 'utils/storefront';
import * as queries from 'utils/storefront/queries';

@customElement('matching-set')
class MatchingSet extends LitElement {
  @state()
  accessor productColorGrouping = {};

  @state()
  accessor colorOptionElements = [];

  @state()
  accessor variantElements = [];

  @state()
  accessor dropdownInputs = [];

  @property({ type: Array, attribute: 'app-color-variants' })
  accessor appColorVariants;

  @property({ type: String, attribute: 'product-handle' })
  accessor productHandle;

  @property({ type: String, attribute: 'add-to-bag-text' })
  accessor addToBagText;

  constructor() {
    super();
    this.storefront = Storefront.getInstance();
  }

  connectedCallback() {
    super.connectedCallback();
    this._buildColorSectionCache();
    this.colorOptionElements = this.querySelectorAll(
      '[data-color-product-handle]',
    );
    this.colorOptionElements.forEach((element) => {
      element.addEventListener('click', this._handleColorClick);
    });
    this.dropdownInputs = this.querySelectorAll('[data-dropdown-input]');
    this.selectedVariantText = this.querySelector(
      '[data-selected-variant-text]',
    );
    this.variantInput = this.querySelector(
      '[data-matching-set-selected-variant-id]',
    );
    this.variantElements = this.querySelectorAll(
      '[data-matching-set-variant-id]',
    );
    this.variantElements.forEach((element) => {
      element.addEventListener('click', this._handleVariantClick);
    });
    this.matchingSetFormElement = this.querySelector(
      '[data-matching-set-form]',
    );
    this.addToCarButton = this.querySelector('[data-add-to-cart-button]');
    this.setupInputs();
    document.addEventListener('click', this._handleClickOutside);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.colorOptionElements.forEach((element) => {
      element.removeEventListener('click', this._handleColorClick);
    });
    this.colorOptionElements = [];

    this.variantElements.forEach((element) => {
      element.removeEventListener('click', this._handleVariantClick);
    });
    this.variantElements = [];
    this.cleanupInputs();
    document.removeEventListener('click', this._handleClickOutside);
  }

  _handleClickOutside = (event) => {
    const { target } = event;
    let clickedInsideInput = false;
    for (const input of this.dropdownInputs) {
      if (input.contains(target)) {
        clickedInsideInput = true;
        break;
      }
      // Also check if the label for this input was clicked
      const label = this.ownerDocument.querySelector(
        `label[for="${input.id}"]`,
      );
      if (label && (label === target || label.contains(target))) {
        clickedInsideInput = true;
        break;
      }
    }
    if (!clickedInsideInput) {
      this._closeInputDropdown();
    }
  };

  _handleFormSubmit = async (event) => {
    event.preventDefault();
    const attributes = [];
    const form = event.target;
    const formData = new FormData(form);
    const data = Object.fromEntries(formData.entries());
    const { id: variantId } = data;

    const responseByHandleUSD = await this.storefront.request({
      query: queries.productByHandleUSD(),
      variables: { handle: this.productHandle },
    });

    const usdCompareAtPrice =
      responseByHandleUSD?.data?.productByHandle?.compareAtPriceRange
        ?.minVariantPrice?.amount || '0.0';

    if (usdCompareAtPrice !== '0.0') {
      attributes.push({
        key: '_compare_at_price',
        value: parseFloat(usdCompareAtPrice).toFixed(2),
      });
    }

    if (data.final_sale === 'true') {
      attributes.push({
        key: '_tag_:final-sale',
        value: data?.final_sale_metafield || 'final-sale',
      });
    }

    const payload = {
      merchandiseId: variantId,
      quantity: 1,
      attributes,
    };

    const addToCartEvent = new CustomEvent('cart:add', {
      detail: {
        payload,
        origin,
        component_name: '',
      },
    });
    document.dispatchEvent(addToCartEvent);
  };

  _closeInputDropdown = () => {
    this.dropdownInputs.forEach((input) => {
      input.checked = false;
    });
  };

  setupInputs = () => {
    this.dropdownInputs.forEach((input) => {
      input.addEventListener('change', this.handleInputChange);

      if (this.selectedInputId) {
        input.checked = input.id === this.selectedInputId;
      }
    });
  };

  cleanupInputs = () => {
    this.dropdownInputs.forEach((input) => {
      input.removeEventListener('change', this.handleInputChange);
    });
  };

  handleInputChange = (e) => {
    const input = e.target;
    if (input.checked) {
      this.selectedInputId = input.id;

      this.dropdownInputs.forEach((i) => {
        if (i !== input) {
          i.checked = false;
        }
      });
    }
  };

  _handleVariantClick = (event) => {
    const target = event.target.closest('[data-matching-set-variant-id]');
    if (!target) return;
    const variantId = target.getAttribute('data-matching-set-variant-id');
    if (!variantId) return;
    const variantText = target.getAttribute('data-variant-title');

    this.variantInput.value = variantId;
    this.selectedVariantText.textContent = variantText;

    this._updateAddToCartButton();
    this._closeInputDropdown();
  };

  _updateAddToCartButton = () => {
    this.addToCarButton.textContent = this.addToBagText || 'Add to Bag';
    this.addToCarButton.disabled = false;
  };

  _handleColorClick = (event) => {
    const target = event.target.closest('[data-color-product-handle]');
    if (!target) return;
    const handle = target.getAttribute('data-color-product-handle');
    if (!handle) return;
    const textMarkup = this.productColorGrouping[handle];
    if (!textMarkup) {
      console.warn(`No color grouping found for handle: ${handle}`);
      return;
    }
    this.replaceWith(textMarkup);
  };

  _buildColorSectionCache = async () => {
    if (!this.appColorVariants) return;

    const fetchPromises = this.appColorVariants.map(async (handle) => {
      if (!this.productColorGrouping[handle]) {
        const response = await fetch(
          // eslint-disable-next-line max-len
          `${window.Shopify.routes.root}products/${handle}?view=matching-set`,
        );
        const text = await response.text();
        const parser = new DOMParser();
        const dom = parser.parseFromString(text, 'text/html');
        const matchingSetElement = dom.querySelector('matching-set');
        if (!matchingSetElement) {
          console.warn(`No matching-set element found for handle: ${handle}`);
          return;
        }
        this.productColorGrouping = {
          ...this.productColorGrouping,
          [handle]: matchingSetElement,
        };
      }
    });
    await Promise.all(fetchPromises);
  };

  render() {
    return html` <slot @submit=${this._handleFormSubmit}></slot> `;
  }
}

export default MatchingSet;
